/**
 * The two lists a commercial's sheet picks from: « Type client » and « Qualification
 * commerciale ». Add a value, or remove one nobody carries — the server refuses to remove a
 * value a commercial still has, and the message says so.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Input } from "@hardauto/core-ui";

import type { SalesRepOption, SalesRepOptionKind } from "../../api";
import { NAMESPACE } from "../../i18n";

const KINDS: readonly SalesRepOptionKind[] = ["client_type", "qualification"];

export function ListsDialog({
  options,
  mayWrite,
  onAdd,
  onRemove,
  onClose,
}: {
  options: readonly SalesRepOption[];
  mayWrite: boolean;
  onAdd: (kind: SalesRepOptionKind, label: string) => Promise<boolean>;
  onRemove: (option: SalesRepOption) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [drafts, setDrafts] = useState<Record<SalesRepOptionKind, string>>({
    client_type: "",
    qualification: "",
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("salesReps.lists.title")}
      description={t("salesReps.lists.intro")}
      closeLabel={t("salesReps.lists.close")}
    >
      <div className="hardauto-rep-lists">
        {KINDS.map((kind) => {
          const values = options.filter((option) => option.kind === kind);
          const title = t(`salesReps.lists.${kind}`);
          return (
            <section key={kind} className="hardauto-rep-lists__list" aria-label={title}>
              <h3 className="hardauto-rep-lists__title">{title}</h3>
              {values.length === 0 ? (
                <p className="hardauto-cell__muted">{t("salesReps.lists.empty")}</p>
              ) : (
                <ul className="hardauto-rep-lists__values">
                  {values.map((option) => (
                    <li key={option.id}>
                      <span>{option.label}</span>
                      {mayWrite ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t("salesReps.lists.remove", { value: option.label })}
                          onClick={() => onRemove(option)}
                        >
                          ×
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              {mayWrite ? (
                <form
                  className="hardauto-rep-lists__add"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const label = drafts[kind].trim();
                    if (!label) return;
                    void onAdd(kind, label).then((added) => {
                      if (added) setDrafts((now) => ({ ...now, [kind]: "" }));
                    });
                  }}
                >
                  <Input
                    aria-label={t("salesReps.lists.newValue", { list: title })}
                    placeholder={t("salesReps.lists.placeholder")}
                    value={drafts[kind]}
                    maxLength={80}
                    onChange={(event) =>
                      setDrafts((now) => ({ ...now, [kind]: event.target.value }))
                    }
                  />
                  <Button type="submit" size="sm">
                    {t("salesReps.lists.add")}
                  </Button>
                </form>
              ) : null}
            </section>
          );
        })}
      </div>
    </Dialog>
  );
}
